import 'dayjs/locale/fr';

import { SxProps, Theme } from '@mui/material';
import {
  DateValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  UseFormRegisterReturn,
  Validate,
} from 'react-hook-form';

import { language } from '@/ui/i18n';

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  label?: string;
  onChange?: (
    value: Dayjs | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => void;
  value?: string;
  sx?: SxProps<Theme>;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  referenceDate?: dayjs.Dayjs; // reference date to show specific month if value and default value are empty
  error?: boolean;
  helperText?: string;
  control?: Control<TFieldValues>;
  name: string;
  validate?: Validate<string, any> | Record<string, Validate<string, any>>; //eslint-disable-line
  required?: boolean | string;
}

let locale = '';
if (language === 'fr') {
  locale = 'fr';
} else if (language === 'en') {
  locale = 'en';
} else {
  locale = 'fr'; // fallback
}

export function FSDateField<TFieldValues extends FieldValues = FieldValues>(
  props: IProps<TFieldValues>,
) {
  if (props.control) {
    return <Controlled_FSDateField {...props} />;
  } else {
    return <Basic_FSDateField {...props} />;
  }
}

function Controlled_FSDateField<
  TFieldValues extends FieldValues = FieldValues,
>({
  label,
  value,
  sx,
  minDate,
  maxDate,
  referenceDate,
  error,
  helperText,
  control,
  name,
  validate,
  required,
}: IProps<TFieldValues>) {
  return (
    <Controller
      control={control}
      name={name as FieldPath<TFieldValues>}
      rules={{ validate, required }}
      render={({ field }) => (
        <DateField
          label={label}
          value={value}
          onChange={field.onChange}
          sx={sx}
          minDate={minDate}
          maxDate={maxDate}
          referenceDate={referenceDate}
          error={error}
          helperText={helperText}
          register={control?.register(name as FieldPath<TFieldValues>)}
        />
      )}
    />
  );
}

function Basic_FSDateField<TFieldValues extends FieldValues = FieldValues>({
  label,
  onChange,
  value,
  sx,
  minDate,
  maxDate,
  referenceDate,
  error,
  helperText,
}: IProps<TFieldValues>) {
  return (
    <DateField
      label={label}
      value={value}
      onChange={onChange}
      sx={sx}
      minDate={minDate}
      maxDate={maxDate}
      referenceDate={referenceDate}
      error={error}
      helperText={helperText}
    />
  );
}

function DateField({
  label,
  value,
  register,
  sx,
  minDate,
  maxDate,
  referenceDate,
  error,
  helperText,
  onChange,
}: {
  label?: string;
  value?: string;
  register?: UseFormRegisterReturn;
  sx?: SxProps<Theme>;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  referenceDate?: dayjs.Dayjs;
  error?: boolean;
  helperText?: string;
  onChange?: (
    value: Dayjs | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => void;
}) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <DatePicker
        label={label}
        value={value ? dayjs(value) : null}
        {...register}
        sx={{ my: 1, ...sx }}
        minDate={minDate}
        maxDate={maxDate}
        referenceDate={referenceDate}
        onChange={onChange}
        slotProps={{
          textField: {
            error: error || false,
            helperText,
          },
        }}
      />
    </LocalizationProvider>
  );
}
